import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Portal R4: the same write repeated within a few seconds (double click / retry) reuses one Idempotency-Key → one record server-side.
const recent = new Map();
const fingerprint = (m, url, data) => {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return `${m}|${url}|${[...data.entries()].map(([k, v]) => `${k}=${typeof v === 'string' ? v : `${v.name}:${v.size}`}`).join('&')}`;
  }
  return `${m}|${url}|${typeof data === 'string' ? data : JSON.stringify(data ?? null)}`;
};
const idemKey = (m, url, data) => {
  const fp = fingerprint(m, url, data);
  const now = Date.now();
  const hit = recent.get(fp);
  if (hit && now - hit.t < 8000) return hit.key;
  const key = (window.crypto?.randomUUID?.() || `${now}-${Math.random().toString(16).slice(2)}`);
  recent.set(fp, { key, t: now });
  if (recent.size > 200) recent.delete(recent.keys().next().value);
  return key;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Portal upload policy (Settings → Client Portal): checked before sending so clients get the friendly message instantly.
let uploadCfg;
let uploadCfgAt = 0;
export const setUploadPolicy = (u) => { uploadCfg = u || null; uploadCfgAt = Date.now(); };
const policy = async () => {
  if (uploadCfg !== undefined && Date.now() - uploadCfgAt < 60000) return uploadCfg;
  try { const r = await api.get('/api/portal/config'); setUploadPolicy(r.data.uploads); } catch { setUploadPolicy(null); }  // staff → 403 → no client-side check
  return uploadCfg;
};
const reject = (detail) => { const e = new Error(detail); e.response = { status: 422, data: { detail } }; e.clientValidation = true; return e; };
const checkUpload = async (data) => {
  const files = [...data.values()].filter((v) => typeof File !== 'undefined' && v instanceof File);
  if (!files.length) return;
  const p = await policy();
  if (!p) return;
  for (const f of files) {
    if (f.size > p.max_mb * 1024 * 1024) throw reject(`“${f.name}” is ${(f.size / 1048576).toFixed(1)} MB. Files must be ${p.max_mb} MB or smaller (portal upload limit).`);
    const ext = f.name.includes('.') ? f.name.split('.').pop().toLowerCase() : '';
    if (!p.accepted.includes(ext)) throw reject(`“${f.name}” isn't an accepted file type. Accepted: ${[...p.accepted].sort().join(', ')}.`);
  }
};

// Request interceptor to add token
api.interceptors.request.use(
  async (config) => {
    const token = sessionStorage.getItem('access_token');
    sessionStorage.setItem('kv_last_api', String(Date.now()));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const entity = sessionStorage.getItem('kv_entity');
    if (entity) config.headers['X-Entity-Id'] = entity;
    // Let the browser set the multipart boundary (a JSON default would serialise FormData as JSON and drop fields)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      await checkUpload(config.data);
    }
    const m = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch'].includes(m) && !(config.url || '').includes('/api/auth/') && !config.headers['Idempotency-Key']) {
      config.headers['Idempotency-Key'] = idemKey(m, config.params ? `${config.url}?${new URLSearchParams(config.params).toString()}` : config.url, config.data);  // query params (e.g. ?fmt=) are part of the request identity
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Practice email not connected: the server didn't send an email this action would have sent – say so plainly.
    if (response.headers?.['x-email-not-sent']) {
      import('sonner').then(({ toast }) => toast.warning('Email not sent – your practice email isn’t connected. Connect it in Settings → Storage & Email.', { id: 'email-not-sent' }));
    }
    return response;
  },
  async (error) => {
    if (error.response?.headers?.['x-email-not-sent']) {
      import('sonner').then(({ toast }) => toast.warning('Email not sent – your practice email isn’t connected. Connect it in Settings → Storage & Email.', { id: 'email-not-sent' }));
    }
    // Sensitive operation: ask SessionGuard to re-authenticate, then retry once.
    if (error.response?.data?.detail === 'STEP_UP_REQUIRED' && !error.config?._steppedUp) {
      try {
        await new Promise((resolve, reject) => window.dispatchEvent(new CustomEvent('kv-step-up', { detail: { resolve, reject } })));
        return api({ ...error.config, _steppedUp: true });
      } catch {
        return Promise.reject(error);
      }
    }
    // Never hijack the login call itself — 401 there means bad credentials or
    // MFA_REQUIRED, which Login.jsx must handle to show the code prompt.
    const isAuthCall = (error.config?.url || '').includes('/api/auth/');
    const det = error.response?.data?.detail;
    if ((det === 'PRACTICE_PENDING_APPROVAL' || det === 'PRACTICE_REJECTED') && window.location.pathname !== '/practice-pending') {
      window.location.href = '/practice-pending';
      return Promise.reject(error);
    }
    if (det === 'PRACTICE_SUSPENDED') sessionStorage.setItem('kv_practice_suspended', '1');
    if (error.response?.status === 401 && !isAuthCall && !error.config?.skipAuthRedirect) {
      // Token expired or invalid - only clear session for this tab
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('organization');
      // Friendly re-login: come back to the same page; drafts (messages, questionnaire) are kept locally / server-side
      sessionStorage.setItem('kv_return_to', window.location.pathname + window.location.search);
      sessionStorage.setItem('kv_session_expired', '1');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getMe: () => api.get('/api/me'),
};

export const orgAPI = {
  list: () => api.get('/api/orgs'),
  create: (data) => api.post('/api/orgs', data),
  switch: (orgId) => api.post('/api/orgs/switch', null, { params: { org_id: orgId } }),
};

export const usersAPI = {
  list: () => api.get('/api/users'),
  create: (data) => api.post('/api/users', data),
  update: (userId, data) => api.patch(`/api/users/${userId}`, data),
};

export const mfaAPI = {
  status: () => api.get('/api/mfa/status'),
  setup: () => api.post('/api/mfa/setup'),
  verify: (code) => api.post('/api/mfa/verify', { code }),
  disable: (code) => api.post('/api/mfa/disable', { code }),
};

// Module 6 — Audit log viewer (admin-only)
export const auditAPI = {
  list: (params = {}) => api.get('/api/audit-logs', { params }),
};

// Module 7 — Email status + test send
export const emailAPI = {
  status: () => api.get('/api/email/status'),
  sendTest: (to_email) => api.post('/api/email/test', { to_email: to_email || null }),
};

// Module 8 — Reminder system (draft → manual send)
export const remindersAPI = {
  list: (params = {}) => api.get('/api/compliance/reminders', { params }),
  generate: (deadlineId) => api.post(`/api/compliance/${deadlineId}/reminders/generate`),
  edit: (reminderId, data) => api.patch(`/api/compliance/reminders/${reminderId}`, data),
  send: (reminderId) => api.post(`/api/compliance/reminders/${reminderId}/send`),
};

// Compliance deadlines (used by the reminders screen)
export const complianceAPI = {
  listDeadlines: (params = {}) => api.get('/api/compliance', { params }),
};

// Feature flag: financial import is disabled for now (client sync moved to Xero Practice
// Manager). Code kept intact — set to true to re-enable the buttons everywhere.
export const FINANCIALS_ENABLED = false;

// Module K #9 — Xero financial import
export const xeroFinancialsAPI = {
  importForClient: (clientId) => api.post(`/api/clients/${clientId}/xero/import-financials`),
  importAll: () => api.post('/api/xero/import-financials/all'),
  bulkStatus: () => api.get('/api/xero/import-financials/status'),
  list: (clientId) => api.get(`/api/clients/${clientId}/xero/financials`),
  get: (clientId, reportId) => api.get(`/api/clients/${clientId}/xero/financials/${reportId}`),
};

export const xeroAPI = {
  // Connection status for the current org
  getStatus: () => api.get('/api/integrations/xero/status'),
  testCall: () => api.get('/api/integrations/xero/test-call'),
  // Fetches the Xero authorize URL (sends JWT); caller then navigates the browser to it.
  connect: () => api.get('/api/integrations/xero/connect'),
  disconnect: () => api.post('/api/integrations/xero/disconnect'),
  // Manually pull contacts from Xero into the portal. full=true ignores the watermark.
  pull: (full = true) => api.post(`/api/integrations/xero/pull?full=${full}`),
};

export const xpmAPI = {
  // Xero Practice Manager (XPM 3.1) — separate connection from the accounting Xero link.
  getStatus: () => api.get('/api/integrations/xpm/status'),
  getConfig: () => api.get('/api/integrations/xpm/config'),
  connect: () => api.get('/api/integrations/xpm/connect'),
  disconnect: () => api.post('/api/integrations/xpm/disconnect'),
  syncClients: () => api.post('/api/integrations/xpm/sync/clients'),
  getSyncStatus: () => api.get('/api/integrations/xpm/sync/status'),
  importCsv: (formData) =>
    api.post('/api/integrations/xpm/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export default api;
